/// <reference types="vite/client" />
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

// Discover MDX posts at build time. Expect each MDX to export `meta` with `title`, `date`, and optional `excerpt`.
const modules = import.meta.glob("/content/blog/**/*.mdx", {
  eager: true,
}) as Record<string, any>;

type PostMeta = {
  title: string;
  date: string;
  excerpt?: string;
  slug: string;
};

// Extract post metadata from the modules
const posts: PostMeta[] = Object.entries(modules)
  .map(([path, mod]) => {
    const slug = path.replace("/content/blog/", "").replace(".mdx", "");
    return {
      slug,
      meta: (mod.meta || {}) as PostMeta,
    };
  })
  .filter((p) => p.meta && p.meta.title)
  .sort((a, b) => (a.meta.date || "").localeCompare(b.meta.date || ""))
  .map((p) => ({
    ...p.meta,
    slug: p.slug,
  }));

export function BlogIndexPage() {
  const original = [
    {
      slug: "what-is-robotics",
      image: "/media/blogs/what-is-vex-robotics.png",
      title:
        "What Is VEX Robotics? A Complete Parent's Guide to Educational Robotics",
      excerpt:
        "Discover how VEX Robotics introduces students to STEM through hands-on learning, programming, and competitive challenges.",
      category: "Getting Started",
    },
    {
      slug: "why-vex-robotics-gives-students-an-edge-on-college-applications",
      image: "/media/blogs/why-vex-robotics.png",
      title: "Why VEX Robotics Gives Students an Edge on College Applications",
      excerpt:
        "Learn how participation in VEX Robotics competitions can significantly enhance college applications and demonstrate valuable skills.",
      category: "College Prep",
    },
    {
      slug: "which-program-is-right-for-my-child",
      image: "/media/blogs/why-we-started-valkyrie-robotics.png",
      title: "VEX IQ vs. V5 - Which program is right for my child?",
      excerpt:
        "Compare VEX IQ and V5 programs to find the perfect robotics experience for your child's age and skill level.",
      category: "Program Guide",
    },
  ];

  return (
    <>
      <SEO
        title="VEX Robotics Blog"
        description="Stay updated with the latest VEX Robotics news, tips, and insights. Learn about competitive robotics, STEM education, and student success stories from Valkyrie Robotics."
        canonical="/blog"
        keywords={[
          "VEX Robotics blog",
          "robotics news",
          "STEM education blog",
          "competitive robotics tips",
          "robotics coaching blog",
          "student robotics success",
          "VEX IQ blog",
          "VEX V5 blog",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="text-center space-y-6">
              <motion.h1
                className="text-4xl md:text-5xl font-bold tracking-tight text-white"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Valkyrie Robotics Blog
              </motion.h1>
              <motion.p
                className="text-xl text-brand-400 font-semibold max-w-3xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                Insights, guides, and stories from the world of competitive
                robotics
              </motion.p>
              <motion.p
                className="text-lg text-gray-300 leading-relaxed max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                Discover expert insights on VEX Robotics, STEM education,
                competition strategies, and how robotics can shape your child's
                future.
              </motion.p>
            </div>
          </div>
        </section>

        {/* Blog Posts Section */}
        <section className="mx-auto max-w-6xl px-4 pb-24">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
              Featured Articles
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto">
              Explore our comprehensive guides and insights to help you
              understand VEX Robotics and make informed decisions for your
              child's education.
            </p>
          </motion.div>

          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {original.map((post, index) => (
              <motion.div
                key={post.slug}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={`/blog/${post.slug}`}
                  className="group block rounded-2xl border border-neutral-800 bg-neutral-900/50 hover:border-neutral-700 hover:bg-neutral-800/30 transition-all duration-300 overflow-hidden h-full flex flex-col"
                >
                  <div className="aspect-[16/9] overflow-hidden relative">
                    <div className="absolute inset-0 bg-gradient-to-t from-neutral-900/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
                    <img
                      src={post.image}
                      alt={post.title}
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      onError={(e) => {
                        (e.currentTarget as HTMLImageElement).src =
                          "/media/hero.jpg";
                      }}
                    />
                    <div className="absolute top-3 left-3">
                      <span className="inline-block px-3 py-1 text-xs font-medium text-brand-400 bg-neutral-900/80 rounded-full border border-neutral-700">
                        {post.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <h2 className="text-xl font-semibold tracking-tight text-gray-100 group-hover:text-white transition-colors duration-300 mb-3 line-clamp-2">
                      {post.title}
                    </h2>
                    <p className="text-gray-300 text-sm leading-relaxed mb-4 flex-1 line-clamp-3">
                      {post.excerpt}
                    </p>
                    <div className="mt-auto">
                      <div className="flex items-center justify-between">
                        <span className="text-brand-400 text-sm font-medium group-hover:text-brand-300 transition-colors duration-300">
                          Read Article →
                        </span>
                        <div className="w-6 h-6 text-brand-400 group-hover:translate-x-1 transition-transform duration-300">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* Call to Action Section */}
          <motion.div
            className="mt-20 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
              <h3 className="text-2xl font-bold tracking-tight mb-4 text-gray-100">
                Ready to Get Started?
              </h3>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto mb-8">
                Have questions about VEX Robotics or want to learn more about
                our programs? Book a free trial session or contact us to discuss
                how we can help your child succeed.
              </p>
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <motion.button
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Free Trial Session
                  </motion.button>
                  <motion.a
                    href="/contact"
                    className="w-full sm:w-auto rounded-lg border-2 border-neutral-600 text-gray-200 px-8 py-4 text-lg font-medium transition-all duration-150 hover:scale-105 hover:border-brand-500 hover:text-brand-400 active:scale-95"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Contact Us
                  </motion.a>
                </div>
                <FreeTrialSchedule />
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </>
  );
}
